import * as moment from 'moment-timezone';
import {useEffect, useState, useCallback} from "react";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {Box, LinearProgress} from "@mui/material";
import {blue, grey} from '@mui/material/colors';
import {useLazyQuery} from "@apollo/client";

import {useTimezone} from '../hooks/useTimezone';
import useBigCalendar from "../hooks/useBigCalendar";
import useFirstDayOfTheWeek, {DEFAULT_START_DAY} from "../hooks/useFirstDayOfTheWeek";

import ServiceTypeTitle from '../components/common/ServiceTypeTitle';
import PerformerMetrics from "../components/common/PerformerMetrics";
import CustomAlert from "../components/CustomAlert";
import ShiftPlanManagementDialog from '../components/shift-plans/ShiftPlanManagementDialog';
import SchedulePlannerToolbar from '../components/shift-plans/SchedulePlannerToolbar';
import ShiftPlanDialog from '../components/shift-plans/ShiftPlanDialog';

import {GET_BOOKING_STATS, GET_SHIFT_PLANS} from "../queries/GraphQLQueries";
import {DATE_ONLY_FORMAT} from '../config/DateTimeConstants';
import {retrieveServiceTypeIcon} from '../helpers/ServiceTypeUtils';
import {processShiftPlanData} from '../helpers/ShiftPlanHelper';

const TIME_GROUPING_INTERVAL = 'DAILY';

const calendarDateCellStyle = {
    position: 'relative',
    height: '100%',
    width: '100%',
    border: '0.031rem solid',
    borderColor: grey[300],
};

const performerBookingAndMetricsStyle = {
    position: 'absolute',
    bottom: '-0.063rem',
    backgroundColor: 'white',
    color: 'black',
    padding: '0.1rem',
    fontSize: '0.875rem',
    textAlign: 'center',
    width: '100%',
    zIndex: 5,
};

const cellDisabledGreyColor = grey[200];
const todayCellBlueColor = blue[50];

export default function SchedulePlanner() {

    const DARK_BLUE = blue[700];

    const {timezone} = useTimezone();
    const {calendarHeight} = useBigCalendar();
    useFirstDayOfTheWeek(DEFAULT_START_DAY);
    
    const [errorMessage, setErrorMessage] = useState(null);
    const [shiftPlans, setShiftPlans] = useState();
    const [shiftPlansByDate, setShiftPlansByDate] = useState(new Map());
    const [performerMetricsByDate, setPerformerMetricsByDate] = useState(new Map());
    const [currentView, setCurrentView] = useState("month");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [shouldRefreshShiftPlans, setShouldRefreshShiftPlans] = useState(false);
    const [currentDate, setCurrentDate] = useState(moment());
    const [showTimeblocks, setShowTimeblocks] = useState(true);
    const [showTimeblockDialog, setShowTimeblockDialog] = useState(false);

    const handleCalendarDaySelection = date => {
        setSelectedDate(date);

        if (showTimeblocks) {
          setShowTimeblockDialog(true);
        } else {
          setDialogOpen(true);
        }
      };

    const handleCloseDialog = () => setDialogOpen(false);

    const [fetchShiftPlans, {loading, error, data}] = useLazyQuery(GET_SHIFT_PLANS, {fetchPolicy: 'network-only'});

    const handleRefresh = () => {
        setShouldRefreshShiftPlans(true);
    };

    useEffect(() => {
        fetchShiftPlans({
            variables: {
                startTime: moment(currentDate).tz(timezone).startOf(currentView).toISOString(),
                endTime: moment(currentDate).tz(timezone).endOf(currentView).toISOString()
            }
        });
        setShouldRefreshShiftPlans(false);
    }, [shouldRefreshShiftPlans, currentDate, currentView]);

    useEffect(() => {
        if (error) {
            setErrorMessage({children: error.message, severity: 'error'});
        }

        const processedData = processShiftPlanData(data, timezone);
        
        const mappedShiftPlansWithJSX = processedData.mappedShiftPlans.map(shiftPlan => ({
            ...shiftPlan,
            title: <ServiceTypeTitle 
                title={shiftPlan.title} 
                serviceTypeIcons={retrieveServiceTypeIcon(shiftPlan.serviceTypeKeycodes)} 
                serviceTypeDescriptions={shiftPlan.serviceTypeDescriptions} 
            />
        }));
        
        setShiftPlans(mappedShiftPlansWithJSX);
        setShiftPlansByDate(processedData.shiftPlansByDateMap);
        setPerformerMetricsByDate(processedData.performerMetricsByDate);
    }, [data, timezone]);

    const [fetchBookingStats, {data: bookingStatsData}] = useLazyQuery(GET_BOOKING_STATS, {fetchPolicy: 'network-only'});

    useEffect(() => {
        fetchBookingStats({
            variables: {
                serviceTypeKeycode: process.env.REACT_APP_SCHEDULE_METRIC_SERVICE_TYPE,
                from: moment(currentDate).tz(timezone).startOf(currentView).toISOString(),
                to: moment(currentDate).tz(timezone).endOf(currentView).toISOString(),
                grouping: TIME_GROUPING_INTERVAL,
                timezone: timezone
            }
        });
    }, [data, timezone]);

    const handleCloseSnackbar = useCallback(() => setErrorMessage(null), []);
    const localizer = momentLocalizer(moment);

    const getDateCellBackgroundColor = (isInCurrentMonth, isToday) => {

        let cellBackgroundColor = 'white';

        if (isToday) {
            cellBackgroundColor = todayCellBlueColor;
        } else if (!isInCurrentMonth) {
            cellBackgroundColor = cellDisabledGreyColor;
        }

        return cellBackgroundColor;
    }

    const sessionStyle = (event) => {
        let customBorderStyle = `0.07rem solid ${DARK_BLUE}`;
        let customBackgroundColor = 'white';
        let fontColor = 'black';

        if (event.enrollments.some(enrollment => enrollment.availability !== null)) {
            customBackgroundColor = DARK_BLUE;
            fontColor = 'white';
        }

        let timeslotBackgroundStyle = {
            backgroundColor: customBackgroundColor,
            border: customBorderStyle,
            color: fontColor,
        };

        return {className: "", style: timeslotBackgroundStyle};
    };

    const handleCalendarNavigate = (date, view) => {
        setCurrentView(view);
        setCurrentDate(date);
    };

    const performerMetricCellWrapper = useCallback(({children, value}) => {

        const startOfMonth = moment(currentDate).tz(timezone).startOf('month');
        const endOfMonth = moment(currentDate).tz(timezone).endOf('month');
        const cellDate = moment(value).tz(timezone);
        const today = moment().tz(timezone).startOf('day');

        const isInCurrentMonth = cellDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
        const isToday = cellDate.isSame(today, 'day');

        const backgroundColor = getDateCellBackgroundColor(isInCurrentMonth, isToday);

        return (
            <Box sx={{...calendarDateCellStyle, backgroundColor}}>
              {children}
              {
                isInCurrentMonth
                &&
                <Box sx={performerBookingAndMetricsStyle}>
                    <PerformerMetrics 
                        shiftPlans={shiftPlansByDate.get(moment(value).tz(timezone).format(DATE_ONLY_FORMAT)) || []} 
                        currentDate={value} 
                        bookingStats={bookingStatsData?.bookingStats}
                        performerMetrics={performerMetricsByDate.get(moment(value).tz(timezone).format(DATE_ONLY_FORMAT))} />
                </Box>
              }
          </Box>
        );
    }, [currentDate, timezone, shiftPlansByDate, bookingStatsData, performerMetricsByDate]);

    return (
        <>
            <Box>
                {loading && <LinearProgress/>}
                {!loading && <Box sx={{my: 0.5}}/>}
                <Calendar
                    localizer={localizer}
                    popup={true}
                    defaultView={Views.MONTH}
                    events={shiftPlans}
                    style={calendarHeight}
                    eventPropGetter={sessionStyle}
                    onNavigate={handleCalendarNavigate}
                    onSelectSlot={handleCalendarDaySelection}
                    tooltipAccessor={null}
                    selectable
                    views={['month']}
                    components={{
                        toolbar: (toolbarProps) => (
                            <SchedulePlannerToolbar
                                onRefresh={handleRefresh}
                                toolbar={toolbarProps}
                                showTimeblocks={showTimeblocks}
                                setShowTimeblocks={setShowTimeblocks}/>
                        ),
                        dateCellWrapper: performerMetricCellWrapper}}/>
            </Box>
            <ShiftPlanManagementDialog 
                openShiftPlanManagementDialog={dialogOpen} 
                handleClose={handleCloseDialog} 
                selectedDate={selectedDate} 
                shouldRefreshShiftPlans={() => setShouldRefreshShiftPlans(true)}/>

            <ShiftPlanDialog
                open={showTimeblockDialog}
                onClose={() => setShowTimeblockDialog(false)}
                selectedDate={selectedDate}
                shouldRefreshShiftPlans={() => setShouldRefreshShiftPlans(true)}
                showTimeblocks={showTimeblocks}
            />
            <CustomAlert alertMessage={errorMessage} handleCloseCustomAlert={handleCloseSnackbar}/>
        </>
    );
};